import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Eraser } from "lucide-react";

/**
 * Lightweight signature canvas. Returns base64 PNG via ref.toDataURL().
 * Props:
 *   - value: optional base64 PNG to display initially
 *   - onChange: optional callback(base64) when stroke ends
 *   - height: px, default 140
 *   - label: optional small label
 *   - testId: optional data-testid
 */
const SignaturePad = forwardRef(({ value, onChange, height = 140, label, testId, disabled = false }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasContent, setHasContent] = useState(!!value);

  useImperativeHandle(ref, () => ({
    toDataURL: () => (hasContent ? canvasRef.current?.toDataURL("image/png") : null),
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      setHasContent(false);
      onChange?.(null);
    },
    isEmpty: () => !hasContent,
  }));

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;
    if (value) {
      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasContent(true);
      };
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const p = getPos(e);
    const last = lastPointRef.current;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasContent) setHasContent(true);
  };

  const end = () => {
    if (!drawingRef.current || disabled) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    if (canvasRef.current && onChange) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasContent(false);
    onChange?.(null);
  };

  return (
    <div className="w-full" data-testid={testId}>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold uppercase tracking-overline text-slate-500">{label}</label>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-30"
          >
            <Eraser size={11} strokeWidth={1.5} />
            Löschen
          </button>
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ height: `${height}px`, width: "100%", touchAction: "none", cursor: disabled ? "not-allowed" : "crosshair" }}
        className="border border-slate-300 bg-white block"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!hasContent && (
        <p className="text-xs text-slate-400 mt-1 italic">Hier unterschreiben (mit Maus oder Finger)</p>
      )}
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
